export type ProfileAccount = 'seeker' | 'company' | 'hiring_agent' | 'creator'

/**
 * Which "account" the user is currently viewing in the Profile tab. Defaults
 * to users.last_account from the API (the last switcher session). Seeker and
 * business users can switch between those two personas; hiring agent is only
 * available for users who signed up as an HR agent.
 *
 * Switching seeker ↔ company resets account-scoped caches (discovery location,
 * showcase refresh) so UI never keeps the previous identity's data.
 *
 * "creator" is a client workspace for online-store tooling (not a backend
 * signup account_type yet).
 */
export function useProfileAccount() {
  const { user, persistLastAccount } = useAuth()
  const discovery = useDiscoveryLocation()
  const { refreshToken } = useShowcaseComposer()

  /** Bumped on every account switch so views can drop in-memory caches. */
  const generation = useState('profile-account-generation', () => 0)

  function defaultAccount(): ProfileAccount {
    const type = user.value?.lastAccount ?? user.value?.accountType
    return type === 'company' || type === 'hiring_agent' ? type : 'seeker'
  }

  function canUseHiringAgent(): boolean {
    return user.value?.accountType === 'hiring_agent'
  }

  function resolveAccount(next: ProfileAccount): ProfileAccount {
    // Online stores live under the business account — never keep "creator" mode.
    if (next === 'creator') return 'company'
    // Seeker / business signups cannot enter the hiring-agent persona.
    if (next === 'hiring_agent' && !canUseHiringAgent()) {
      return user.value?.accountType === 'company' ? 'company' : 'seeker'
    }
    return next
  }

  const account = computed<ProfileAccount>(() => resolveAccount(defaultAccount()))

  function setAccount(next: ProfileAccount, options?: { persist?: boolean }) {
    next = resolveAccount(next)

    const previous = account.value
    const alreadyActive = previous === next && user.value?.lastAccount === next
    if (alreadyActive) return

    if (
      options?.persist !== false
      && user.value
      && (next === 'seeker' || next === 'company' || next === 'hiring_agent')
    ) {
      void persistLastAccount(next)
    }

    // Company and hiring-agent share the same employer location row — keep the
    // pin when switching between those two so we don't re-hit /api/company.
    const stayedOnEmployer =
      (previous === 'company' || previous === 'hiring_agent')
      && (next === 'company' || next === 'hiring_agent')

    if (!stayedOnEmployer) {
      discovery.reset()
      void discovery.ensureSeeded()
    }

    refreshToken.value++
    generation.value++
  }

  return { account, setAccount, generation }
}
